import React, { ReactNode } from "react";
import { Trans } from "react-i18next";
import {
  Box,
  BoxProps,
  IconButton,
  Theme,
  Typography,
  TypographyVariant,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import isPropValid from "@emotion/is-prop-valid";
import Dots from "@icons/myicons/dots.svg?react";
import { ActionsMenu } from "../navigation";

interface MenuButtonProps {
  hasMenuItems?: boolean;
}

const MenuButton = styled(IconButton, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || prop !== "hasMenuItems",
})<MenuButtonProps>`
  visibility: ${({ hasMenuItems }) => (hasMenuItems ? "visible" : "hidden")};
`;

interface Props extends Omit<BoxProps, "title"> {
  title?: ReactNode;
  subtitle?: ReactNode;
  actionButton?: ReactNode;
  mobileMenu?: ReactNode;
  isMainPage?: boolean;
  isAddButton?: boolean;
  addButtonBreakpoint?: "sm" | "md";
  variant?: TypographyVariant;
}

export const Header = ({
  title,
  variant = "h4",
  mobileMenu,
  subtitle,
  actionButton,
  isMainPage,
  isAddButton,
  addButtonBreakpoint = "sm",
  ...rest
}: Props) => {
  const theme = useTheme();
  const menuRef = React.createRef<HTMLDivElement>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const showAddInSecondRow =
    useMediaQuery((theme: Theme) =>
      theme.breakpoints.down(addButtonBreakpoint)
    ) && isAddButton;

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box
      display="flex"
      flexDirection={showAddInSecondRow ? "column" : "row"}
      justifyContent="space-between"
      alignItems={showAddInSecondRow ? "flex-start" : "center"}
      className="main-header"
      mb={isXs && isMainPage ? 4 : 2}
      {...rest}
    >
      <Box>
        {typeof title === "string" ? (
          <Typography
            variant={variant}
            component="h1"
            mr={2}
            mb={subtitle ? 1 : 0}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {typeof subtitle === "string" ? (
          <Typography variant="body1" color="textSecondary" mt={isXs ? 3 : 4}>
            <Trans
              i18nKey={subtitle}
              components={{
                span: (
                  <span
                    style={{
                      color: theme.palette.text.upgrade,
                      fontWeight: 600,
                    }}
                  />
                ),
              }}
            />
          </Typography>
        ) : (
          subtitle
        )}
      </Box>
      <Box flexShrink={0} width={isXs && isAddButton ? "100%" : "initial"}>
        {isXs && mobileMenu && !isAddButton && (
          <>
            <MenuButton
              data-testid="open-menu-button"
              aria-controls="simple-menu"
              aria-haspopup="true"
              hasMenuItems={React.Children.count(mobileMenu) > 0}
              onClick={handleOpenMenu}
            >
              <Dots />
            </MenuButton>
            <ActionsMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl}>
              <div ref={menuRef}>{mobileMenu}</div>
            </ActionsMenu>
          </>
        )}
        {!showAddInSecondRow &&
          ((isAddButton ? !showAddInSecondRow : !isXs) || !mobileMenu) &&
          actionButton}
        {showAddInSecondRow && (
          <Box
            sx={{
              width: "100%",
              marginTop: "20px",
            }}
            color="primary"
          >
            {actionButton}
          </Box>
        )}
      </Box>
    </Box>
  );
};
